import api from '../../api';
import {
  addDonationToLocalStorage,
  donationGTMTrigger,
  getDonationFromLocalStorage,
  removeDonationFromLocalStorage,
} from '../../helpers';

const state = {
  isDonating: false,
  canCheckStatus: false,
  isCheckingStatus: false,
  hasDonated: false,
  donation: null,
  guestLink: '',
  pennyBridgeId: null,
  description: '',
  totalDonations: null,
  totalsLoading: false,
  noDonationsAllowed: false,
  availableFunds: null,
  gettingAvailableFunds: false,
};

const mutations = {
  setGuestLink(currentState, guestLink = '') {
    currentState.guestLink = guestLink;
  },

  setOrder(state, data) {
    state.order = data;
  },

  setIsDonating(state, isDonating = true) {
    state.isDonating = isDonating;
  },

  setDonation(state, donation = null) {
    state.donation = donation;
  },

  setHasDonated(state, hasDonated = false) {
    state.hasDonated = hasDonated;
  },

  setCanCheckStatus(state, canCheckStatus = false) {
    state.canCheckStatus = canCheckStatus;
  },

  setIsCheckingStatus(state, isCheckingStatus = false) {
    state.isCheckingStatus = isCheckingStatus;
  },

  setPennyBridgeId(state, pennyBridgeId = null) {
    state.pennyBridgeId = pennyBridgeId;
  },

  setDescription(state, description = '') {
    state.description = description;
  },

  setTotalDonations(state, totalDonations = '') {
    state.totalDonations = totalDonations;
  },

  setTotalsLoading(state, totalsLoading = '') {
    state.totalsLoading = totalsLoading;
  },

  setNoDonationsAllowed(state, noDonationsAllowed = false) {
    state.noDonationsAllowed = noDonationsAllowed;
  },

  setAvailableFunds(state, availableFunds = null) {
    state.availableFunds = availableFunds;
  },

  setGettingAvailableFunds(state, gettingAvailableFunds = false) {
    state.gettingAvailableFunds = gettingAvailableFunds;
  },
};

const actions = {
  addDonation({ commit, state }, data = {}) {
    commit('setIsDonating');

    return new Promise((resolve, reject) => {
      api
        .post('add-donation', data)
        .then((donation) => {
          commit('setDonation', donation);
          addDonationToLocalStorage(donation);
          setTimeout(() => commit('setCanCheckStatus', true), 1000);

          return resolve(donation);
        })
        .catch((error) => {
          commit('setIsDonating', false);
          commit('setDonation', null);

          return reject(error);
        });
    });
  },

  checkDonationStatus({ commit, state }) {
    commit('setIsCheckingStatus', true);

    if (!state.donation) {
      // state missing, try to get it from local storage.
      commit('setDonation', getDonationFromLocalStorage());
    }

    if (!state.donation?.id) {
      return;
    }

    const { id, hash } = state.donation;

    return new Promise((resolve, reject) => {
      api
        .post(`get-donation-status/${id}`, { hash })
        .then(({ comment, statusIsPaid, statusIsPending, guestLink }) => {
          commit('setGuestLink', statusIsPaid ? guestLink || '' : '');

          if (statusIsPaid) {
            commit('setIsDonating', false);
            commit('setHasDonated', true);
            commit('setDonation', comment);
            commit('setCanCheckStatus', false);
            addDonationToLocalStorage(comment);
            donationGTMTrigger(comment);
          } else if (!statusIsPending) {
            commit('setDonation', null);
            commit('setIsDonating', false);
            commit('setHasDonated', false);
          }

          commit('setIsCheckingStatus', false);

          return resolve();
        })
        .catch((error) => {
          commit('setIsDonating', false);
          commit('setHasDonated', false);
          commit('setIsCheckingStatus', false);
          commit('setDonation', null);

          return reject(error);
        });
    });
  },

  resetDonation({ commit }) {
    removeDonationFromLocalStorage();
    commit('setDonation', null);
    commit('setHasDonated', false);
    commit('setIsDonating', false);
    commit('setIsCheckingStatus', false);
    commit('setCanCheckStatus', false);
  },

  getTotalDonations({ commit }, obituaryId) {
    commit('setTotalsLoading', true);

    return new Promise((resolve, reject) => {
      api
        .get(`get-total-donations/${obituaryId}`)
        .then((data) => {
          commit('setTotalDonations', data);
          commit('setTotalsLoading', false);

          return resolve();
        })
        .catch((error) => reject(error));
    });
  },

  getAvailableFunds({ commit }) {
    commit('setGettingAvailableFunds', true);

    return new Promise((resolve, reject) => {
      api
        .get('get-available-funds')
        .then((data) => {
          commit('setAvailableFunds', data);
          commit('setGettingAvailableFunds', false);
          return resolve();
        })
        .catch((error) => {
          commit('setGettingAvailableFunds', false);
          return reject(error);
        });
    });
  },
};

export default {
  state,
  mutations,
  actions,
  namespaced: true,
};
